import { useEffect, useState } from 'react';
import { useAppDispatch } from '@/store';
import { fetchPermissionGroups, grantPermissionToRole } from '@/store/thunks/roleThunks';
import type { Role, Permission, PermissionGroup } from '@/types';
import { Modal, Button } from '@/components/ui';

interface RolePermissionsModalProps {
  role: Role | null;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

function permissionKey(p: Permission): string {
  return String(p._key ?? p.key ?? p.id);
}

function rolePermissionKeys(role: Role | null): Set<string> {
  const list = role?.permissions ?? [];
  const set = new Set<string>();
  list.forEach((p: Permission) => set.add(permissionKey(p)));
  return set;
}

export default function RolePermissionsModal({ role, open, onClose, onSuccess }: RolePermissionsModalProps) {
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroup[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (open) {
      setFetching(true);
      dispatch(fetchPermissionGroups())
        .unwrap()
        .then((groups) => setPermissionGroups(Array.isArray(groups) ? groups : []))
        .catch(() => setPermissionGroups([]))
        .finally(() => setFetching(false));
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (open && role) {
      setSelectedKeys(rolePermissionKeys(role));
    }
  }, [open, role]);

  const togglePermission = (p: Permission) => {
    const key = permissionKey(p);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAllInGroup = (group: PermissionGroup) => {
    const perms = group.permissions ?? [];
    const keys = perms.map(permissionKey);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const allSelected = keys.every((k) => next.has(k));
      if (allSelected) keys.forEach((k) => next.delete(k));
      else keys.forEach((k) => next.add(k));
      return next;
    });
  };

  const selectAllGlobal = () => {
    const allKeys = permissionGroups.flatMap((g) => (g.permissions ?? []).map(permissionKey));
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      const allSelected = allKeys.length > 0 && allKeys.every((k) => next.has(k));
      if (allSelected) allKeys.forEach((k) => next.delete(k));
      else allKeys.forEach((k) => next.add(k));
      return next;
    });
  };

  const handleSave = async () => {
    if (!role?.id) return;
    setLoading(true);
    try {
      for (const group of permissionGroups) {
        const parent = group.parent;
        if (!parent?.id) continue;
        const selected = (group.permissions ?? []).filter((p) =>
          selectedKeys.has(permissionKey(p))
        );
        await dispatch(
          grantPermissionToRole({
            roleId: role.id,
            permissionParentId: parent.id,
            permissions: selected,
          })
        ).unwrap();
      }
      onSuccess?.();
      onClose();
    } catch {
      // toast already in thunk
    } finally {
      setLoading(false);
    }
  };

  const roleLabel = role?.nom ?? role?.name ?? role?.code ?? 'Rôle';
  const allKeys = permissionGroups.flatMap((g) => (g.permissions ?? []).map(permissionKey));
  const allSelected = allKeys.length > 0 && allKeys.every((k) => selectedKeys.has(k));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Permissions : ${roleLabel}`}
      size="full"
    >
      <div className="flex w-full flex-col gap-4">
        {fetching ? (
          <p className="text-sm text-slate-500">Chargement des permissions…</p>
        ) : permissionGroups.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun groupe de permissions.</p>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
              <span className="text-sm font-medium text-slate-700">Sélection globale</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={selectAllGlobal}
              >
                {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {permissionGroups.map((group) => {
                const parent = group.parent;
                const perms = group.permissions ?? [];
                const parentName = parent?.nom ?? parent?._key ?? 'Permissions';
                const groupKeys = perms.map(permissionKey);
                const groupAllSelected = groupKeys.length > 0 && groupKeys.every((k) => selectedKeys.has(k));
                return (
                  <div
                    key={parent?.id ?? parentName}
                    className="rounded-xl border border-slate-200 bg-white shadow-sm"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-4 py-3">
                      <h3 className="font-semibold text-slate-800">{parentName}</h3>
                      <button
                        type="button"
                        onClick={() => selectAllInGroup(group)}
                        className="text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
                      >
                        {groupAllSelected ? 'Désélectionner tout' : 'Tout sélectionner'}
                      </button>
                    </div>
                    <div className="flex flex-col gap-2 p-4 max-h-[30vh] overflow-y-auto">
                      {perms.map((p) => {
                        const key = permissionKey(p);
                        const checked = selectedKeys.has(key);
                        const label = p.nom ?? p._key ?? p.key ?? `Permission #${p.id}`;
                        return (
                          <label
                            key={p.id}
                            className="flex cursor-pointer items-center gap-2 rounded-md border border-slate-100 px-2.5 py-2 text-sm text-slate-700 transition-colors hover:bg-slate-50 hover:border-slate-200"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePermission(p)}
                              className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="min-w-0 truncate" title={label}>{label}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2 border-t border-slate-200 pt-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
          Annuler
        </Button>
        <Button type="button" onClick={handleSave} loading={loading} disabled={fetching || permissionGroups.length === 0}>
          Enregistrer
        </Button>
      </div>
    </Modal>
  );
}
